import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { RouteComponentProps } from "react-router-dom";
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonBackButton,
  IonFooter,
  IonToggle,
  useIonToast,
} from "@ionic/react";
import { useQuery } from "@tanstack/react-query";

import { validateMessage } from "../../../utils/validators";

import ContactsApi from "../../../api/contacts";
import ChatDetail from "../../../components/chat-detail";
import UploadPhotoModal from "../../../components/UploadPhotoModal";
import FileInput from "../../../components/common/file-input";
import ChatInput from "../../../components/common/chat-input";
import ContactPlatform from "../../../components/common/contact-platform";

import { ChatInputRef, Settings, Message, Contact } from "../../../interfaces";

import SparksSvg from "../../../theme/images/sparks.svg";

import "./Chat.css";

//Stores
import useContactsStore from "../../../store/contacts";

// Hooks
import { useHandleNewMessage, useSelectedContactData } from "../../../hooks";

const Chat: React.FC<ILayoutProps> = (props: ILayoutProps) => {
  const { history } = props;
  const chatInputRef = useRef<ChatInputRef>(null);
  const [isFocus, setIsFocus] = useState(0);
  const [scrollEscalations, setScrollEscalations] = useState(0);
  const [files, setFiles] = useState<File[]>([]);
  const [docFiles, setDocFiles] = useState<File[]>([]);
  const [isLoadingUpload, setIsLoadingUpload] = useState(false);
  const [isOpenUpload, setIsOpenUpload] = useState(false);
  const [isOpenDocsUpload, setIsDocsOpenUpload] = useState<boolean>(false);
  const [replyTo, setReplyTo] = useState<Message | null>(null);

  const hiddenFileInput = useRef<HTMLInputElement | null>(null);
  const hiddenDocInput = useRef<HTMLInputElement | null>(null);

  const id = useMemo(() => props.match.params.id, [props.match.params]);
  const contactData = useSelectedContactData(id);
  const [body, setBody] = useState("");

  const [present] = useIonToast();

  const intl = useIntl();

  const { updateContactsDb } = useContactsStore();

  // Contact detail event handler
  const handleDetail = useCallback(
    (c: Contact) => {
      console.log("setting up contact detail");
      updateContactsDb([c]);
    },
    [updateContactsDb]
  );

  const { data: contactInfo, isLoading: isRequestingContact } = useQuery({
    queryFn: () => ContactsApi.getContact(id),
    queryKey: ["getContact", id],
    refetchOnWindowFocus: false,
    enabled: !!id,
  });

  useEffect(() => {
    if (contactInfo && contactInfo?._id && !isRequestingContact) {
      handleDetail(contactInfo);
    }
  }, [contactInfo, isRequestingContact]);

  useHandleNewMessage();

  useEffect(() => {
    if (contactData.messages.length) {
      const lastMessage = contactData.messages[contactData.messages.length - 1];
      if (lastMessage.id) {
        ContactsApi.readMessages(contactData.contact.id, lastMessage.id);
      }
    }
  }, [contactData.messages.length]);

  const sendMessage = async (content: string) => {
    setBody("");
    setReplyTo(null);
    const { err } = await ContactsApi.postMessages(id, {
      content,
      replyTo: replyTo?.id,
    });
    if (err) {
      if (err.error) {
        return present({
          message: err.error?.[intl.locale],
          duration: 6000,
          color: "warning",
          position: "bottom",
        });
      }
      console.log(err);
    }
  };

  const enableOrDisableContact = async (enabled: boolean) => {
    const { err } = await ContactsApi.putEnabled(id, { enabled });
    if (err) {
      console.log(err);
    }
  };

  const solveEscalations = async (id: string) => {
    const { err } = await ContactsApi.putSolveEscalations(id);
    if (err) {
      console.log(err);
    }
  };

  const handleAutoChange = (event: CustomEvent<{ checked: boolean }>) => {
    return enableOrDisableContact(event.detail.checked);
  };

  const handleSendSubmit = async () => {
    const { err } = validateMessage(body);
    if (err) {
      return;
    }
    return sendMessage(body);
  };

  const handleSendChange = (body: string) => setBody(body);

  const sendPhotos = async (content: FormData) => {
    setIsLoadingUpload(true);
    const { err } = await ContactsApi.uploadMedia(id, content);
    setIsLoadingUpload(false);
    if (err) {
      if (err.error) {
        return present({
          message: err.error?.[intl.locale],
          duration: 6000,
          color: "warning",
          position: "bottom",
        });
      }
      console.log(err);
    }
    setIsOpenUpload(false);
    return onHandleModalClose();
  };

  const scrollToEscalation = (messageId?: string) => {
    if (messageId) {
      const messageEl = document.getElementById(messageId);
      if (messageEl) {
        return messageEl.scrollIntoView({
          block: "center",
          behavior: "smooth",
        });
      }
    }
    return setScrollEscalations(scrollEscalations + 1);
  };

  /* This is extremely risky to call a [0] index */
  const escalation = contactData.contact.escalated ? (
    <IonToolbar className="tool-bg-warning">
      <div className="chat-escalation-title-container">
        <div className="fw-semibold-1" style={{ textTransform: "capitalize" }}>
          <FormattedMessage id="esc.escalation" /> ·{" "}
          {contactData.escalations?.[0]?.type?.replace(/_/g, " ")}{" "}
        </div>
        <div>{contactData.escalations?.[0]?.details}</div>
        <div className="mt-2 color-visito-2">
          <a
            onClick={() =>
              scrollToEscalation(contactData.escalations?.[0]?.message)
            }
          >
            <FormattedMessage id="esc.tapToView" />
          </a>
        </div>
      </div>
    </IonToolbar>
  ) : null;
  /* This is extremely risky to call a [0] index */

  const openSendPhoto = () => {
    hiddenFileInput.current?.click();
  };

  const openSendDoc = () => {
    hiddenDocInput.current?.click();
  };

  const onChangeFileInput = (
    e: React.ChangeEvent<HTMLInputElement>,
    type: "photo" | "doc"
  ) => {
    setIsLoadingUpload(false);
    const fileList = e.target.files;
    if (fileList && fileList.length > 0) {
      const files = [...fileList];

      if (type === "photo") {
        setFiles(files);
        return setIsOpenUpload(true);
      }

      setDocFiles(files);
      return setIsDocsOpenUpload(true);
    }
  };

  const onHandleModalClose = () => {
    setIsDocsOpenUpload(() => false);
    setIsOpenUpload(() => false);

    if (hiddenFileInput?.current?.value) {
      hiddenFileInput.current.value = "";
    }
    if (hiddenDocInput.current?.value) {
      hiddenDocInput.current.value = "";
    }

    setFiles([]);
    return setDocFiles([]);
  };

  const runConversational = async () => {
    try {
      const { err } = await ContactsApi.runConversational(
        contactData.contact.id
      );
      if (err) {
        console.log(err);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const inputFocused = () => setTimeout(() => setIsFocus(isFocus + 1), 650);

  return (
    <IonPage id="main-content" className="chat">
      <IonHeader>
        {escalation}
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton
              defaultHref="/chats"
              text={intl.formatMessage({ id: "common.back" })}
            />
          </IonButtons>
          <IonTitle
            onClick={(e) => {
              e.preventDefault();
              history.push(`/chat/${id}/contact`);
            }}
          >
            <div className="chat-title-container">
              <ContactPlatform
                defaultClass="user-platform"
                contact={contactData.contact}
              />
              <div className="chat-title-profile">
                <div className="fs-4 title-name">
                  {contactData.contact.name}
                </div>
                {props.settings.multiAccount && (
                  <div className="fs-2 color-step-550 fw-semibold-0 title-company">
                    {contactData.contact.companies.map((c) => c.name).join(",")}
                  </div>
                )}
              </div>
            </div>
          </IonTitle>
          <IonButtons slot="end">
            <img
              src={SparksSvg}
              width={20}
              style={{ marginRight: "6px" }}
              alt=""
            />
            <IonToggle
              checked={contactData.contact.enabled}
              onIonChange={handleAutoChange}
              className="visito-toggle"
              style={{ marginRight: "5px" }}
            ></IonToggle>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <ChatDetail
        scroll={isFocus}
        solveEscalations={solveEscalations}
        scrollEscalations={scrollEscalations}
        replyTo={({ message }: { message: Message }) => {
          setReplyTo(message);
        }}
        isLoading={isRequestingContact}
        selectedId={id}
      />
      <IonFooter>
        <IonToolbar>
          <div className="submit-mobile">
            <FileInput
              ref={hiddenFileInput}
              accept="image/png, image/jpeg, image/webp"
              multiple
              onChange={(e) => onChangeFileInput(e, "photo")}
              style={{ display: "none" }}
            />
            <FileInput
              ref={hiddenDocInput}
              accept="application/pdf, .pdf"
              onChange={(e) => onChangeFileInput(e, "doc")}
              style={{ display: "none" }}
            />

            <ChatInput
              ref={chatInputRef}
              id="send-body"
              value={body}
              placeholder={intl.formatMessage({
                id: "chats.newMessageInput",
              })}
              channel={contactData.contact.type}
              onInputChange={handleSendChange}
              onOpenPhotoInput={openSendPhoto}
              onOpenDocInput={openSendDoc}
              onRunConversational={runConversational}
              onBeforeSubmit={handleSendSubmit}
              onAfterFocus={inputFocused}
              disabled={contactData.contact.blocked}
              cleanReplyTo={() => setReplyTo(null)}
              replyTo={replyTo}
            />
          </div>
        </IonToolbar>
      </IonFooter>
      <UploadPhotoModal
        show={isOpenUpload || isOpenDocsUpload}
        handleClose={onHandleModalClose}
        files={isOpenDocsUpload ? docFiles : files}
        upload={sendPhotos}
        mode="mobile"
        areDocs={isOpenDocsUpload}
        sending={isLoadingUpload}
      />
    </IonPage>
  );
};

interface ILayoutProps extends RouteComponentProps<{ id: string }> {
  settings: Settings;
}

export default Chat;
